import api from '@functions/api'

import type { InvoiceSummaryByStatusModel, InvoiceSummaryModel } from '@models/invoiceSummary'
import type { AppDispatch } from '@redux/store'
import type {
  Action, ActionResult, EntityOptions, Errors,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/invoiceSummary/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/invoiceSummary/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/invoiceSummary/FETCH_FAILURE'

export type InvoiceSummaryModuleState = {
  errors: string[],
  loaded: boolean,
  loading: boolean,
  result: InvoiceSummaryModel | {},
}

// Initial State
const initialState: InvoiceSummaryModuleState = {
  errors: [],
  loaded: false,
  loading: false,
  result: {},
}

// Actions
export function fetchRequest(): Action {
  return {
    type: FETCH_REQUEST,
  }
}

type FetchRequestPayload = {
  result: InvoiceSummaryModel,
  type: string,
}

export function fetchSuccess(payload: FetchRequestPayload): Action {
  return {
    type: FETCH_SUCCESS,
    result: payload.result,
  }
}

export function fetchFailure(errors: Errors = []): Action {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

// Action Creators
export function fetchInvoiceSummary(options: EntityOptions) {
  return (dispatch: AppDispatch): Promise<ActionResult<InvoiceSummaryModel>> => {
    dispatch(fetchRequest())

    const promise = api('/invoices/summary', options)
      .then(({ data }) => {
        dispatch(fetchSuccess({ result: data }))

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function fetchInvoiceSummaryByStatus(options: EntityOptions) {
  return (dispatch: AppDispatch): Promise<ActionResult<InvoiceSummaryByStatusModel>> => {
    dispatch(fetchRequest())

    const promise = api('/invoices/list-per-status', options)
      .then(({ data }) => {
        dispatch(fetchSuccess({ result: data }))

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: InvoiceSummaryModuleState = initialState,
  action: Action = { type: '' },
): InvoiceSummaryModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        errors: [],
        loaded: true,
        loading: false,
        result: action.result || state.result,
      }
    case FETCH_FAILURE:
      return {
        ...state,
        errors: action.errors || [],
        loaded: false,
        loading: false,
      }
    default:
      return state
  }
}
